import { CapManageControllerContext } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/CapManageEntityFlow.tsx';
import { zodResolver } from '@hookform/resolvers/zod';
import { useContext, useEffect, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
	INVESTMENT_PROGRAMME_FORM_FIELDS,
	InvestmentProgrammeFormValues,
} from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/BasicDetails/components/InvestmentProgrammeDrawer/utils/types.ts';
import { PersonDataContext } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/BasicDetails/hooks/usePersonData.ts';
import {
	getAvailablePersonTransactionsForMipRequest,
	getPersonMipOpenTsaWarningRequest,
} from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/BasicDetails/components/InvestmentProgrammeDrawer/apis/useInvestmentProgramme.ts';
import { InvestmentProgrammeFormShape } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/BasicDetails/components/InvestmentProgrammeDrawer/utils/relatedPeopleShape.ts';
import { unlinkInvestmentProgrammeRequest } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/BasicDetails/apis/useInvestmentProgrammes.ts';
import { GetPersonInvestmentProgrammesRes } from '@dealstack/shared/lib/generated';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths.ts';

export const useUnlinkInvestmentProgrammeDrawer = (
	opened: boolean,
	onClose: () => void,
	linkedMip: GetPersonInvestmentProgrammesRes | null
) => {
	const { selectedEntityId } = useContext(CapManageControllerContext);
	const { refetchInvestmentProgrammes } = useContext(PersonDataContext);
	const [unlinkTransactions, setUnlinkTransactions] = useState(false);

	const unlinkPersonFromMipMutation = useMutation({
		mutationFn: unlinkInvestmentProgrammeRequest,
	});

	const unLinkMipForm = useForm<InvestmentProgrammeFormValues>({
		mode: 'all',
		resolver: zodResolver(InvestmentProgrammeFormShape),
	});

	useEffect(() => {
		unLinkMipForm.setValue(INVESTMENT_PROGRAMME_FORM_FIELDS.mip_id, linkedMip?.id ?? '');
	}, [linkedMip]);

	const mipId = useWatch({
		control: unLinkMipForm.control,
		name: INVESTMENT_PROGRAMME_FORM_FIELDS.mip_id,
	});

	const personTransactionsForMipRequest = useQuery({
		queryKey: ['personTransactionsForMip', selectedEntityId, mipId],
		queryFn: () => {
			if (!selectedEntityId) {
				return Promise.resolve([]);
			}

			return getAvailablePersonTransactionsForMipRequest({
				personId: selectedEntityId,
				mipId,
				isUnlink: true,
			});
		},
		enabled: !!(selectedEntityId && mipId),
	});

	const personMipOpenTsaWarningRequest = useQuery({
		queryKey: [
			API_PATHS.PERSON_PERSONID_MIP_MIPID_OPEN_TSA_WARNING_GET,
			selectedEntityId,
			mipId,
		],
		queryFn: () => {
			return getPersonMipOpenTsaWarningRequest({
				personId: selectedEntityId,
				mipId,
			});
		},
		enabled: !!(opened && selectedEntityId && mipId),
	});

	const onFormSubmit = async (formValues: InvestmentProgrammeFormValues) => {
		const { mip_id } = formValues;
		return unlinkPersonFromMipMutation.mutateAsync(
			{
				personId: selectedEntityId,
				mipId: mip_id,
				unlinkTransactions,
			},
			{
				onSuccess: () => {
					void refetchInvestmentProgrammes();
					setUnlinkTransactions(false);
					onClose();
				},
			}
		);
	};

	return {
		unLinkMipForm,
		onFormSubmit,
		isLoading: unlinkPersonFromMipMutation.isPending,
		personTransactionsForMip: personTransactionsForMipRequest.data,
		personMipOpenTsaWarning: personMipOpenTsaWarningRequest.data,
		unlinkTransactions,
		setUnlinkTransactions,
	};
};
